import Tag from "@/components/Tag";
import type { BackgroundCheckOrderUpdate } from "./types";
import OrderDateTimeDisplay from "./OrderDateTimeDisplay";
import { formatOrderProgress, getOrderStatusTagType, sortOrderUpdatesNewestFirst } from "./orderPresentation";

const outlinedStepMarkerClass = "[&_circle]:stroke-sys-brand-secondary [&_path]:stroke-sys-brand-secondary";

interface OrderActivityTimelineProps {
  updates: BackgroundCheckOrderUpdate[];
}

export default function OrderActivityTimeline({ updates }: OrderActivityTimelineProps) {
  const sortedUpdates = sortOrderUpdatesNewestFirst(updates);

  if (sortedUpdates.length === 0) {
    return (
      <p className="text-body-medium text-sys-brand-on-surface-variant">
        No activity has been recorded for this order.
      </p>
    );
  }

  return (
    <ol className="w-full" data-testid="background-check-order-timeline">
      {sortedUpdates.map((update, index) => (
        <li key={update.id} className="flex min-h-[70px] gap-4">
          <div className="flex flex-col items-center pt-1.5">
            <div className={outlinedStepMarkerClass}>
              <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="6" cy="6" r="5" strokeWidth="2" />
              </svg>
            </div>
            {index < sortedUpdates.length - 1 && (
              <div className={`mt-5 ${outlinedStepMarkerClass}`}>
                <svg width="2" height="48" viewBox="0 0 2 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M1 47V1" strokeLinecap="round" strokeDasharray="4 4" />
                </svg>
              </div>
            )}
          </div>

          <div className="mb-6 min-w-0 flex-1">
            <Tag tagType={getOrderStatusTagType(update.status)} size="small" tagStyle="strong">
              {update.status || "Not Started"}
            </Tag>
            <div className="mt-1 text-body-medium text-sys-brand-on-surface-variant">
              <OrderDateTimeDisplay value={update.createdAt} />
            </div>
            <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-body-medium text-sys-brand-on-surface-variant">
              <span>Result: {update.result || "--"}</span>
              <span>Progress: {formatOrderProgress(update.percentComplete)}</span>
            </div>
          </div>
        </li>
      ))}
    </ol>
  );
}
